import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar, MapPin, Clock, Users, DollarSign, Check, X as XIcon, CheckCircle2, ExternalLink, Image as ImageIco, ImageOff } from "lucide-react";
import { EventPaymentDialog } from "@/components/events/EventPaymentDialog";
import { useCardExpansion } from "@/hooks/useCardExpansion";
import { CardThumb } from "@/components/cards/CardThumb";

function fmtDateRange(start, end) {
  if (!start) return "";
  try {
    const s = new Date(start);
    const sStr = s.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
    if (!end) return sStr;
    const e = new Date(end);
    return `${sStr} – ${e.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}`;
  } catch { return start; }
}

/** Return "$25" / "$1,250" / "Free" for a price_cents value. */
function priceLabel(cents) {
  const n = Number(cents) || 0;
  if (n <= 0) return "Free";
  return `$${(n / 100).toLocaleString(undefined, { minimumFractionDigits: (n % 100 === 0 ? 0 : 2), maximumFractionDigits: 2 })}`;
}

/** True when `start_at` has passed but `end_at` hasn't yet. */
function isHappeningNow(ev) {
  if (!ev.start_at || !ev.end_at) return false;
  const now = new Date();
  return new Date(ev.start_at) <= now && now < new Date(ev.end_at);
}

/** Pulsing "Happening now" pill — reused as overlay badge and inline badge. */
function HappeningNowBadge({ overlay }) {
  if (overlay) {
    return (
      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600/90 text-white text-[10px] font-semibold shadow-md backdrop-blur">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
          <span className="relative inline-flex rounded-full h-full w-full bg-white" />
        </span>
        Happening now
      </div>
    );
  }
  return (
    <Badge className="ml-1.5 align-middle text-[9px] px-1.5 h-4 bg-red-600/15 text-red-700 dark:text-red-300 border border-red-500/30 gap-1" data-testid="happening-now-badge">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
        <span className="relative inline-flex rounded-full h-full w-full bg-red-500" />
      </span>
      Live
    </Badge>
  );
}

export default function MemberEvents() {
  const [events, setEvents] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payingEvent, setPayingEvent] = useState(null);
  const [me, setMe] = useState(null);
  const { isExpanded: isImgExpanded, toggle: toggleImage } = useCardExpansion();
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/events", { params: { upcoming: true } });
      setEvents(data || []);
    } catch (err) {
      console.warn("[Events] Failed to load events:", err);
      setEvents([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data || null);
      } catch (err) { console.warn("[Events] Failed to load user:", err); }
    })();
  }, []);

  // Deep-link: auto-open event detail from ?event=<id> query param (e.g. push notification tap)
  useEffect(() => {
    if (!events || events.length === 0) return;
    const eventId = searchParams.get("event");
    if (!eventId) return;
    const target = events.find((e) => e.id === eventId);
    if (target) {
      setExpanded(target);
      // Clear the param so closing the dialog doesn't re-open it
      setSearchParams((prev) => { prev.delete("event"); return prev; }, { replace: true });
    }
  }, [events, searchParams, setSearchParams]);

  const submitRsvp = async (eventId, status) => {
    const current = events.find((e) => e.id === eventId)?.my_rsvp;
    const willClear = current === status;
    setSaving(true);
    try {
      if (willClear) {
        await api.delete(`/events/${eventId}/rsvp`);
      } else {
        await api.post(`/events/${eventId}/rsvp`, { status });
      }
      setEvents((arr) => arr.map((e) => {
        if (e.id !== eventId) return e;
        const wasAttending = e.my_rsvp === "attending";
        const willBeAttending = !willClear && status === "attending";
        let count = e.attending_count || 0;
        if (wasAttending && !willBeAttending) count = Math.max(0, count - 1);
        if (!wasAttending && willBeAttending) count = count + 1;
        // If they cleared attendance, refund path clears my_payment too.
        const nextPayment = willClear || status !== "attending" ? null : e.my_payment;
        return {
          ...e,
          my_rsvp: willClear ? null : status,
          attending_count: count,
          my_payment: nextPayment,
        };
      }));
      setExpanded((cur) => {
        if (!cur || cur.id !== eventId) return cur;
        const wasAttending = cur.my_rsvp === "attending";
        const willBeAttending = !willClear && status === "attending";
        let count = cur.attending_count || 0;
        if (wasAttending && !willBeAttending) count = Math.max(0, count - 1);
        if (!wasAttending && willBeAttending) count = count + 1;
        const nextPayment = willClear || status !== "attending" ? null : cur.my_payment;
        return {
          ...cur,
          my_rsvp: willClear ? null : status,
          attending_count: count,
          my_payment: nextPayment,
        };
      });
      toast.success(
        willClear
          ? (current === "attending" && events.find((e) => e.id === eventId)?.my_payment
              ? "RSVP cleared and payment refunded"
              : "RSVP cleared")
          : (status === "attending" ? "You're attending — thanks!" : "RSVP saved")
      );
      window.dispatchEvent(new CustomEvent("dcc:events-rsvp-changed"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Could not save RSVP");
    } finally {
      setSaving(false);
    }
  };

  const onPaymentSuccess = (eventId, data) => {
    // Flip local state so the "Pay" button becomes a "Paid" pill immediately.
    const payment = {
      paid_at: new Date().toISOString(),
      amount_cents: Number(data?.amount_cents || 0),
      receipt_url: data?.receipt_url || null,
      receipt_number: data?.receipt_number || null,
    };
    setEvents((arr) => arr.map((e) => (e.id === eventId ? { ...e, my_payment: payment, paid_count: (e.paid_count || 0) + 1 } : e)));
    setExpanded((cur) => (cur && cur.id === eventId ? { ...cur, my_payment: payment } : cur));
    toast.success(`Paid ${priceLabel(payment.amount_cents)} — see you there!`);
  };

  const attending = (events || []).filter((e) => e.my_rsvp === "attending");
  const others = (events || []).filter((e) => e.my_rsvp !== "attending");

  return (
    <AppShell variant="member" title="Events">
      <div className="space-y-10 max-w-6xl">
        {/* Attending */}
        <section data-testid="member-events-attending-section">
          <div className="mb-5">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300" /> You&rsquo;re attending
            </div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">
              {attending.length === 0 ? "No events yet" : `${attending.length} upcoming event${attending.length === 1 ? "" : "s"}`}
            </h2>
          </div>
          {events === null ? (
            <Card className="p-10 text-center text-muted-foreground border-dashed rounded-2xl">Loading…</Card>
          ) : attending.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground border-dashed rounded-2xl">
              <Calendar className="mx-auto h-7 w-7 mb-3 opacity-60" />
              RSVP &lsquo;attending&rsquo; to an upcoming event and it&rsquo;ll show up here.
            </Card>
          ) : (
            <EventGrid events={attending} onOpen={setExpanded} isImgExpanded={isImgExpanded} toggleImage={toggleImage} />
          )}
        </section>
      </div>

      <Dialog open={!!expanded} onOpenChange={(v) => { if (!v) setExpanded(null); }}>        <DialogContent className="rounded-2xl max-w-4xl w-[calc(100vw-2rem)] max-h-[92vh] overflow-y-auto p-0" data-testid="member-events-detail-dialog">
          {expanded && (
            <>
              <DialogTitle className="sr-only">{expanded.title}</DialogTitle>
              {expanded.image_url && (
                <div className="w-full bg-[hsl(var(--secondary))] flex items-center justify-center">
                  <img src={expanded.image_url} alt={expanded.title} className="w-full h-auto max-h-[70vh] object-contain" />
                </div>
              )}
              <div className="p-6 sm:p-8 space-y-5">
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] font-semibold">
                  <Clock className="h-3.5 w-3.5" /> {fmtDateRange(expanded.start_at, expanded.end_at)}
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{expanded.title}</h2>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
                  {expanded.location && (<div className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {expanded.location}</div>)}
                  {(expanded.price_cents || 0) > 0 && (
                    <div className="inline-flex items-center gap-1.5"><DollarSign className="h-4 w-4" /> {priceLabel(expanded.price_cents)}</div>
                  )}
                  <div className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /><span className="font-semibold text-foreground">{expanded.attending_count || 0}</span> attending</div>
                </div>
                {expanded.description && (
                  <p className="text-sm sm:text-base text-foreground/80 whitespace-pre-wrap leading-relaxed">{expanded.description}</p>
                )}
                <div className="pt-2 border-t border-border">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">Your RSVP</div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" disabled={saving} onClick={() => submitRsvp(expanded.id, "attending")}
                      className={`h-11 px-5 rounded-full ${expanded.my_rsvp === "attending" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[hsl(var(--secondary))] text-foreground hover:bg-[hsl(var(--secondary))]/70"}`}>
                      <Check className="h-4 w-4 mr-2" />
                      {expanded.my_rsvp === "attending" ? "I'm attending ✓" : "I'm attending"}
                    </Button>
                    <Button type="button" disabled={saving} onClick={() => submitRsvp(expanded.id, "cant_attend")}
                      variant="outline"
                      className={`h-11 px-5 rounded-full ${expanded.my_rsvp === "cant_attend" ? "border-red-500/50 bg-red-500/10 text-red-700 dark:text-red-300" : ""}`}>
                      <XIcon className="h-4 w-4 mr-2" />
                      {expanded.my_rsvp === "cant_attend" ? "Can't attend ✓" : "Can't attend"}
                    </Button>
                  </div>
                  {expanded.my_rsvp && (
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Tap your current choice again to clear your RSVP
                      {expanded.my_payment ? " (your payment will be automatically refunded)" : ""}.
                    </p>
                  )}

                  {/* Payment CTA — appears ONLY when the member has selected
                      "I'm attending" and the event has a price. If they've
                      already paid we show a green receipt pill instead. */}
                  {(expanded.price_cents || 0) > 0 && expanded.my_rsvp === "attending" && (
                    <div
                      className="mt-4 rounded-xl border border-border bg-[hsl(var(--secondary))]/40 p-4"
                      data-testid="event-detail-payment-panel"
                    >
                      {expanded.my_payment ? (
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-300 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                              Paid {priceLabel(expanded.my_payment.amount_cents)}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {new Date(expanded.my_payment.paid_at).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}
                            </div>
                            {expanded.my_payment.receipt_url && (
                              <a
                                href={expanded.my_payment.receipt_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 mt-2 text-[11px] text-[hsl(var(--accent))] hover:underline"
                                data-testid="event-detail-receipt-link"
                              >
                                View receipt <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex-1 min-w-[180px]">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                              Attendance fee
                            </div>
                            <div className="text-lg font-bold tracking-tight mt-0.5">
                              {priceLabel(expanded.price_cents)} <span className="text-xs font-normal text-muted-foreground">AUD</span>
                            </div>
                          </div>
                          <Button
                            type="button"
                            onClick={() => setPayingEvent(expanded)}
                            className="h-11 px-5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90"
                            data-testid="event-detail-pay-button"
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Pay {priceLabel(expanded.price_cents)}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment dialog for the currently expanded event.
          Rendered once at the page root so it survives detail-dialog toggles. */}
      <EventPaymentDialog
        event={payingEvent}
        open={!!payingEvent}
        onOpenChange={(v) => { if (!v) setPayingEvent(null); }}
        buyer={me ? { name: `${me.first_name || ""} ${me.last_name || ""}`.trim(), email: me.email } : null}
        onPaid={(data) => { if (payingEvent) onPaymentSuccess(payingEvent.id, data); }}
      />
    </AppShell>
  );
}


function EventGrid({ events, onOpen, isImgExpanded, toggleImage }) {
  const [expandedCards, setExpandedCards] = useState(new Set());
  const toggleCard = (id) => setExpandedCards((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  return (
    <div className="space-y-3">
      {events.map((ev) => {
        const isPaidEvent = (ev.price_cents || 0) > 0;
        const paid = !!ev.my_payment;
        const showImage = !!isImgExpanded && isImgExpanded(ev.id);
        const happeningNow = isHappeningNow(ev);
        const isExpanded = expandedCards.has(ev.id);
        const openDetail = () => onOpen(ev);
        const handleClick = () => { toggleCard(ev.id); };
        const onKeyDown = (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        };
        return (
          /* Full-width horizontal card. role="button" wraps the whole thing so
             a click anywhere expands the detail dialog — but the poster
             toggle inside stops propagation so it can be used independently. */
          <div
            role="button"
            tabIndex={0}
            key={ev.id}
            onClick={handleClick}
            onKeyDown={onKeyDown}
            className="text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-2xl cursor-pointer block"
            data-testid={`member-events-card-${ev.id}`}
          >
            <Card className="overflow-hidden rounded-2xl hover-lift transition-all group-hover:border-[hsl(var(--accent))]/40">
              {/* Full-width poster reveals when toggled on. */}
              {showImage && ev.image_url && (
                <div className="aspect-[21/9] max-h-[420px] bg-[hsl(var(--secondary))] overflow-hidden relative">
                  <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
                  {happeningNow && <HappeningNowBadge overlay />}
                  {ev.my_rsvp === "attending" && (
                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/95 text-white text-[10px] font-semibold shadow-md backdrop-blur">
                      <Check className="h-3 w-3" /> Attending
                    </div>
                  )}
                  {isPaidEvent && paid && (
                    <div
                      className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[hsl(var(--primary))]/95 text-[hsl(var(--primary-foreground))] text-[10px] font-semibold shadow-md backdrop-blur"
                      data-testid={`member-events-card-paid-${ev.id}`}
                    >
                      <CheckCircle2 className="h-3 w-3" /> Paid
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-stretch gap-4 p-4 relative">
                {!showImage && (<CardThumb
                  src={ev.image_url}
                  alt={ev.title}
                  className={isExpanded ? "h-24 w-32 sm:h-28 sm:w-40 md:h-56 md:w-80 rounded-xl" : "h-14 w-20 sm:h-16 sm:w-24 md:h-32 md:w-48 rounded-lg"}
                />)}
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold tracking-tight ${isExpanded ? "mt-1 text-lg" : "text-base"}`}>
                    {ev.title}
                    {isExpanded && ev.my_rsvp === "attending" && (
                      <Badge className="ml-2 align-middle text-[9px] px-1.5 h-4 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                        <Check className="h-2.5 w-2.5 mr-0.5" /> Attending
                      </Badge>
                    )}
                    {isExpanded && isPaidEvent && paid && (
                      <Badge className="ml-1.5 align-middle text-[9px] px-1.5 h-4 bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30">
                        <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Paid
                      </Badge>
                    )}
                    {isExpanded && happeningNow && <HappeningNowBadge />}
                  </div>
                  {isExpanded && (
                    <>
                      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] font-medium mt-1">
                        <Clock className="h-3 w-3" /> {fmtDateRange(ev.start_at, ev.end_at)}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {ev.location && (<span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.location}</span>)}
                        {isPaidEvent && (
                          <span className="inline-flex items-center gap-1">
                            <DollarSign className="h-3 w-3" /> {priceLabel(ev.price_cents)}
                            {ev.my_rsvp === "attending" && !paid && (
                              <Badge
                                variant="outline"
                                className="ml-1 h-4 text-[9px] px-1.5 border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/10"
                                data-testid={`member-events-card-unpaid-${ev.id}`}
                              >
                                Payment due
                              </Badge>
                            )}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span className="font-semibold text-foreground">{ev.attending_count || 0}</span> attending
                        </span>
                      </div>
                      {ev.description && (
                        <div className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{ev.description}</div>
                      )}
                      <button type="button" onClick={(e) => { e.stopPropagation(); openDetail(); }} className="mt-2 text-xs font-medium text-[hsl(var(--accent))] hover:underline" data-testid={`member-events-card-details-${ev.id}`}>View Details →</button>
                    </>
                  )}
                </div>
                {isExpanded && ev.image_url && toggleImage && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleImage(ev.id); }}
                    className="shrink-0 self-start h-8 w-8 inline-flex items-center justify-center rounded-full border border-border/70 bg-card/90 backdrop-blur text-muted-foreground hover:text-foreground hover:border-border transition-colors"
                    data-testid={`member-events-card-toggle-image-${ev.id}`}
                    aria-expanded={showImage}
                    aria-label={showImage ? "Hide poster" : "Show full poster"}
                    title={showImage ? "Hide poster" : "Show full poster"}
                  >
                    {showImage ? <ImageOff className="h-3.5 w-3.5" /> : <ImageIco className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            </Card>
          </div>
        );
      })}
    </div>
  );
}
